import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from './ui/sheet';
import { Label } from './ui/label';
import {
  Loader2, Search, Users, MapPin, X, CheckCircle2, ShieldCheck, ShieldAlert, Building2,
  Printer, FileText, UserPlus,
} from 'lucide-react';
import PdfPreviewModal from './PdfPreviewModal';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

// ---------- Member search popover ----------
const MemberSearch = ({ onPick, eventId }) => {
  const api = useApi();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookedInfo, setBookedInfo] = useState(null);   // shown as popup when user clicks an already-booked name
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('q', q.trim());
        params.set('limit', '10');
        if (eventId) params.set('event_id', eventId);
        const res = await api.get(`/seat/members/search?${params.toString()}`);
        setResults(res.data?.members || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [q, eventId]);

  const handlePick = (m) => {
    if (m?.existing_booking) {
      // Already booked → show popup with floor + seat; don't carry forward to booking flow
      setBookedInfo({ member: m, booking: m.existing_booking });
      return;
    }
    onPick(m); setQ(''); setResults([]);
  };

  return (
    <div className="relative">
      <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by ITS / Name / HOF (Misaq Done ladies)"
        className="pl-8"
        data-testid="allot-search-input"
      />
      {q.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-80 overflow-y-auto z-50">
          {loading ? (
            <div className="p-3 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-slate-500 text-center">No matches</div>
          ) : (
            results.map(m => {
              const eb = m.existing_booking;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handlePick(m)}
                  className={`w-full text-left px-3 py-2 border-b last:border-b-0 ${eb ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-emerald-50'}`}
                  data-testid={`member-${m.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1">
                        {m.full_name}
                        {eb && <Badge className="bg-amber-600 text-white shrink-0 text-[10px]">Already booked</Badge>}
                      </div>
                      <div className="text-xs text-slate-500">
                        ITS {m.its_id} · age {m.age ?? '—'} · {m.hof_name?.slice(0, 40) || ''}
                      </div>
                      {eb && (
                        <div className="text-[11px] text-amber-800 font-medium mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {eb.floor_name || 'Floor'} · Seat #{eb.seat_no}
                          {eb.event_name && <span className="text-amber-600 font-normal">· {eb.event_name} {eb.event_year || ''}</span>}
                        </div>
                      )}
                    </div>
                    {m.hof_clearance === 'approved' ? (
                      <Badge className="bg-emerald-600 shrink-0"><ShieldCheck className="w-3 h-3 mr-1" />Cleared</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 border-amber-300 shrink-0"><ShieldAlert className="w-3 h-3 mr-1" />Pending</Badge>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}

      <Dialog open={!!bookedInfo} onOpenChange={(o) => !o && setBookedInfo(null)}>
        <DialogContent className="max-w-sm" data-testid="already-booked-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <CheckCircle2 className="w-5 h-5" /> Already Booked
            </DialogTitle>
          </DialogHeader>
          {bookedInfo && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="font-semibold">{bookedInfo.member.full_name}</div>
                <div className="text-xs text-slate-500">ITS {bookedInfo.member.its_id}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-slate-50 border rounded p-2">
                  <div className="text-[10px] uppercase font-semibold text-slate-500">Floor</div>
                  <div className="font-medium flex items-center gap-1"><Building2 className="w-4 h-4 text-blue-600" /> {bookedInfo.booking.floor_name || '—'}</div>
                </div>
                <div className="bg-slate-50 border rounded p-2">
                  <div className="text-[10px] uppercase font-semibold text-slate-500">Seat</div>
                  <div className="font-bold text-lg text-emerald-700">#{bookedInfo.booking.seat_no}</div>
                </div>
              </div>
              {bookedInfo.booking.event_name && (
                <div className="text-xs text-slate-600">
                  Event: <strong>{bookedInfo.booking.event_name} {bookedInfo.booking.event_year || ''}</strong>
                  {bookedInfo.booking.pass_id && <span> · Pass ID: <span className="font-mono">{bookedInfo.booking.pass_id}</span></span>}
                </div>
              )}
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                This member already has a confirmed booking. Use the Cancellation Requests tab if you need to release this seat.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookedInfo(null)} data-testid="already-booked-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Floor Map ----------
const FloorMap = ({ floorLayout, onSeatClick, selectedDummy }) => {
  if (!floorLayout) return null;
  const seatByCell = {};
  (floorLayout.seats || []).forEach(s => { seatByCell[`${s.row},${s.col}`] = s; });
  const disabledByCell = new Set((floorLayout.disabled_cells || []).map(c => `${c.row},${c.col}`));

  return (
    <div className="overflow-auto p-3 bg-slate-50 rounded border" data-testid="allot-floor-map">
      <div className="inline-block">
        <div className="text-center text-[10px] font-semibold text-slate-500 mb-2 select-none">← QIBLA →</div>
        <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${floorLayout.cols}, minmax(0, 1fr))` }}>
          {Array.from({ length: floorLayout.rows }).map((_, r) =>
            Array.from({ length: floorLayout.cols }).map((__, c) => {
              const key = `${r},${c}`;
              const seat = seatByCell[key];
              const isDis = disabledByCell.has(key);
              let bg = 'bg-transparent';
              let label = '';
              let title = `(${r},${c})`;
              if (isDis) bg = 'bg-slate-400';
              else if (seat) {
                title = `Seat #${seat.seat_no}`;
                label = seat.seat_no;
                if (seat.booking_type === 'admin') {
                  bg = 'bg-blue-600 text-white border-blue-800';
                  title += ` · ADMIN · ${seat.member_name || ''}`;
                } else if (seat.booking_type === 'user') {
                  bg = 'bg-red-500 text-white border-red-700';
                  title += ` · USER · ${seat.member_name || ''}`;
                } else if (seat.booking_type === 'manual') {
                  bg = 'bg-purple-600 text-white border-purple-800';
                  title += ` · MANUAL · ${seat.member_name || ''}`;
                } else if (seat.booking_type === 'dummy') {
                  bg = 'bg-orange-400 text-white border-orange-600';
                  title += ` · DUMMY ${seat.member_name || ''}`;
                } else {
                  if (selectedDummy?.has(seat.seat_no)) bg = 'bg-orange-300 border-orange-500';
                  else bg = 'bg-emerald-500 text-white hover:bg-emerald-600 border-emerald-700';
                }
              }
              return (
                <button
                  key={key}
                  type="button"
                  className={`border rounded text-[9px] font-bold leading-none transition-colors ${bg}`}
                  style={{ width: 24, height: 24 }}
                  title={title}
                  onClick={() => seat && onSeatClick(seat)}
                  data-testid={seat ? `seat-${seat.seat_no}` : `cell-${r}-${c}`}
                  disabled={!seat}
                >
                  {label}
                </button>
              );
            })
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs">
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500" /> Available</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500" /> User Booked</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-600" /> Admin Booked</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-orange-400" /> Dummy Reserved</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-purple-600" /> Manual Walk-in</span>
        <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-400" /> Disabled</span>
      </div>
    </div>
  );
};

// ---------- Allotment Panel ----------
export default function SeatAllotmentPanel({ events }) {
  const api = useApi();
  const [eventId, setEventId] = useState('');
  const [floors, setFloors] = useState([]);
  const [floorId, setFloorId] = useState('');
  const [layout, setLayout] = useState(null);
  const [loadingLayout, setLoadingLayout] = useState(false);

  const [mode, setMode] = useState('book'); // 'book' | 'dummy' | 'manual'
  const [chosenMember, setChosenMember] = useState(null);
  const [dummySelection, setDummySelection] = useState(new Set());
  const [dummyLabel, setDummyLabel] = useState('Dummy Reserved');

  // Manual allotment
  const [manualForm, setManualForm] = useState({ name: '', mobile: '', city: '', its_id: '', remarks: '' });

  // Booking info dialog
  const [seatInfo, setSeatInfo] = useState(null);

  // Convert dummy → member dialog state
  const [convertTarget, setConvertTarget] = useState(null);   // booking object
  const [convertMember, setConvertMember] = useState(null);
  const [busyConvert, setBusyConvert] = useState(false);

  // Manual allot dialog
  const [manualSeat, setManualSeat] = useState(null);

  // event/floor preselect
  useEffect(() => { if (!eventId && events?.length > 0) setEventId(events[0].id); }, [eventId, events]);

  const loadFloors = useCallback(async () => {
    if (!eventId) { setFloors([]); return; }
    try {
      const res = await api.get(`/seat/events/${eventId}/floors`);
      const list = res.data?.floors || [];
      setFloors(list);
      if (list.length && !list.find(f => f.id === floorId)) setFloorId(list[0].id);
      if (!list.length) setFloorId('');
    } catch { setFloors([]); }
    // eslint-disable-next-line
  }, [eventId]);
  useEffect(() => { loadFloors(); }, [loadFloors]);

  const loadLayout = useCallback(async () => {
    if (!floorId) { setLayout(null); return; }
    setLoadingLayout(true);
    try {
      const res = await api.get(`/seat/floors/${floorId}/layout`);
      setLayout(res.data?.floor || null);
    } catch { setLayout(null); }
    finally { setLoadingLayout(false); }
    // eslint-disable-next-line
  }, [floorId]);
  useEffect(() => { loadLayout(); }, [loadLayout]);

  const handleSeatClick = async (seat) => {
    if (seat.booked) { setSeatInfo(seat); return; }
    if (mode === 'dummy') {
      setDummySelection(prev => {
        const n = new Set(prev);
        if (n.has(seat.seat_no)) n.delete(seat.seat_no); else n.add(seat.seat_no);
        return n;
      });
      return;
    }
    if (mode === 'manual') {
      setManualSeat(seat);
      setManualForm({ name: '', mobile: '', city: '', its_id: '', remarks: '' });
      return;
    }
    // mode = 'book' → require a chosen member
    if (!chosenMember) {
      toast.error('Pick a member first using the search box');
      return;
    }
    try {
      await api.post(`/seat/floors/${floorId}/book-admin`, { member_id: chosenMember.id, seat_no: seat.seat_no });
      toast.success(`Seat #${seat.seat_no} booked for ${chosenMember.full_name}`);
      setChosenMember(null);
      loadLayout();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Book failed'); }
  };

  const submitManual = async () => {
    if (!manualSeat) return;
    if (!manualForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      await api.post(`/seat/floors/${floorId}/manual-allot`, { ...manualForm, seat_no: manualSeat.seat_no });
      toast.success(`Seat #${manualSeat.seat_no} manually allotted`);
      setManualSeat(null);
      loadLayout();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Manual allot failed'); }
  };

  const submitConvert = async () => {
    if (!convertTarget || !convertMember) return;
    setBusyConvert(true);
    try {
      await api.post(`/seat/bookings/${convertTarget.booking_id}/convert-to-member`, { member_id: convertMember.id });
      toast.success(`Seat #${convertTarget.seat_no} converted to ${convertMember.full_name}`);
      setConvertTarget(null); setConvertMember(null); setSeatInfo(null);
      loadLayout();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Convert failed'); }
    finally { setBusyConvert(false); }
  };

  // Single PDF pass preview
  const [passPreview, setPassPreview] = useState(null);
  // Bulk passes preview
  const [bulkPreview, setBulkPreview] = useState(false);

  const printPass = (seat) => {
    if (!seat?.booking_id) return;
    setPassPreview(seat);
  };

  const printBulk = () => {
    if (!floorId) return;
    setBulkPreview(true);
  };

  const saveDummies = async () => {
    if (dummySelection.size === 0) return;
    try {
      const res = await api.post(`/seat/floors/${floorId}/book-dummy`, {
        seat_nos: Array.from(dummySelection),
        label: dummyLabel || 'Dummy Reserved',
      });
      toast.success(`${res.data?.created?.length || 0} seat(s) reserved`);
      setDummySelection(new Set());
      loadLayout();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reserve failed'); }
  };

  const cancelBooking = async () => {
    if (!seatInfo?.booking_id) return;
    if (!window.confirm(`Cancel booking on seat #${seatInfo.seat_no}?`)) return;
    try {
      await api.delete(`/seat/bookings/${seatInfo.booking_id}`);
      toast.success('Booking cancelled');
      setSeatInfo(null);
      loadLayout();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          className="border rounded px-2 py-1.5 text-sm bg-white"
          data-testid="allot-event-select"
        >
          {(events || []).map(e => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
        <select
          value={floorId}
          onChange={(e) => setFloorId(e.target.value)}
          className="border rounded px-2 py-1.5 text-sm bg-white"
          data-testid="allot-floor-select"
          disabled={!floors.length}
        >
          {floors.length === 0 && <option value="">No floors</option>}
          {floors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <div className="inline-flex rounded-md border bg-white">
          <button
            onClick={() => setMode('book')}
            className={`px-3 py-1.5 text-xs font-medium ${mode === 'book' ? 'bg-blue-600 text-white' : 'text-slate-700'}`}
            data-testid="mode-book"
          >Admin Book</button>
          <button
            onClick={() => setMode('dummy')}
            className={`px-3 py-1.5 text-xs font-medium ${mode === 'dummy' ? 'bg-orange-500 text-white' : 'text-slate-700'}`}
            data-testid="mode-dummy"
          >Dummy Reserve</button>
          <button
            onClick={() => setMode('manual')}
            className={`px-3 py-1.5 text-xs font-medium ${mode === 'manual' ? 'bg-purple-600 text-white' : 'text-slate-700'}`}
            data-testid="mode-manual"
          >Manual Allot</button>
        </div>
        <Button size="sm" variant="outline" onClick={loadLayout} data-testid="allot-refresh">Refresh</Button>
        <Button size="sm" variant="outline" onClick={printBulk} disabled={!floorId} data-testid="allot-bulk-print">
          <Printer className="w-3 h-3 mr-1" /> Print All Passes
        </Button>
      </div>

      {mode === 'book' ? (
        <Card>
          <CardContent className="p-3">
            <div className="text-xs font-semibold text-slate-600 mb-2">1. Pick a member</div>
            <MemberSearch onPick={setChosenMember} eventId={eventId} />
            {chosenMember && (
              <div className="mt-2 flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                <Users className="w-4 h-4 text-emerald-700" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{chosenMember.full_name}</div>
                  <div className="text-xs text-slate-600">ITS {chosenMember.its_id} · age {chosenMember.age ?? '—'} · {chosenMember.hof_name?.slice(0, 40)}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setChosenMember(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            <div className="text-xs text-slate-500 mt-2">
              2. Click any green seat on the map below to assign. Admin booking bypasses all eligibility rules.
            </div>
          </CardContent>
        </Card>
      ) : mode === 'dummy' ? (
        <Card>
          <CardContent className="p-3 space-y-2">
            <div className="text-xs font-semibold text-slate-600">Dummy / VIP Reserve mode</div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={dummyLabel}
                onChange={(e) => setDummyLabel(e.target.value)}
                placeholder="Label (e.g., VIP Reserved)"
                className="flex-1 min-w-[200px]"
                data-testid="dummy-label"
              />
              <Badge variant="outline">{dummySelection.size} selected</Badge>
              <Button size="sm" variant="outline" onClick={() => setDummySelection(new Set())}>Clear</Button>
              <Button size="sm" onClick={saveDummies} disabled={dummySelection.size === 0}
                className="bg-orange-500 hover:bg-orange-600" data-testid="dummy-save">
                Save Reservations
              </Button>
            </div>
            <div className="text-xs text-slate-500">
              Click any green seat to toggle into the reservation. Users will see these as <em>Booked</em>.
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-3">
            <div className="text-xs font-semibold text-slate-600 flex items-center gap-1"><UserPlus className="w-3 h-3" /> Manual / Walk-in Allotment</div>
            <p className="text-xs text-slate-500 mt-1">
              Click any green seat to allot it to a walk-in (non-database) attendee. You'll be prompted for name, mobile, city, etc.
            </p>
          </CardContent>
        </Card>
      )}

      {loadingLayout ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : !layout ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          {floors.length === 0 ? 'No floors for this event — create one in the Events tab.' : 'Select a floor above'}
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold">{layout.name}</h3>
              <Badge variant="outline">{layout.seats.length} seats</Badge>
              <Badge variant="outline">{layout.seats.filter(s => s.booked).length} booked</Badge>
              <Badge variant="outline">{layout.seats.filter(s => !s.booked).length} available</Badge>
            </div>
            <FloorMap floorLayout={layout} onSeatClick={handleSeatClick} selectedDummy={dummySelection} />
          </CardContent>
        </Card>
      )}

      {/* Seat info / cancel dialog */}
      <Dialog open={!!seatInfo} onOpenChange={(o) => !o && setSeatInfo(null)}>
        <DialogContent className="max-w-sm" data-testid="allot-seat-info">
          <DialogHeader><DialogTitle>Seat #{seatInfo?.seat_no} — {seatInfo?.booking_type?.toUpperCase()}</DialogTitle></DialogHeader>
          <div className="space-y-1 text-sm">
            <div><strong>Member:</strong> {seatInfo?.member_name || '—'}</div>
            <div><strong>ITS:</strong> {seatInfo?.its_id || '—'}</div>
            <div><strong>HOF:</strong> {seatInfo?.hof_name || '—'}</div>
            <div className="text-xs text-slate-500">Booked: {seatInfo?.booked_at ? new Date(seatInfo.booked_at).toLocaleString() : '—'}</div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {seatInfo?.booking_type === 'dummy' ? (
              <Button onClick={() => { setConvertTarget(seatInfo); setConvertMember(null); }} data-testid="allot-convert-btn">
                <UserPlus className="w-3 h-3 mr-1" /> Convert to Member
              </Button>
            ) : (
              <Button variant="outline" onClick={() => printPass(seatInfo)} data-testid="allot-print-pass-btn">
                <Printer className="w-3 h-3 mr-1" /> Print Pass
              </Button>
            )}
            <Button variant="outline" onClick={() => setSeatInfo(null)}>Close</Button>
            <Button variant="destructive" onClick={cancelBooking} data-testid="cancel-booking-btn">Cancel Booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert dummy → member side drawer */}
      <Sheet open={!!convertTarget} onOpenChange={(o) => !o && setConvertTarget(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="convert-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Convert Dummy Seat #{convertTarget?.seat_no} → Member
            </SheetTitle>
            <SheetDescription>
              Search and pick a registered member to assign this dummy-reserved seat. The dummy hold will be released and the booking re-created under the chosen member.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="bg-orange-50 border border-orange-200 rounded p-3 text-xs space-y-1">
              <div className="font-semibold text-orange-900">Current Dummy Hold</div>
              <div><strong>Seat:</strong> #{convertTarget?.seat_no}</div>
              <div><strong>Label:</strong> {convertTarget?.member_name || '—'}</div>
              <div><strong>Booked:</strong> {convertTarget?.booked_at ? new Date(convertTarget.booked_at).toLocaleString() : '—'}</div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Find Member</Label>
              <div className="mt-1.5"><MemberSearch onPick={setConvertMember} /></div>
            </div>
            {convertMember && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
                <Users className="w-4 h-4 text-emerald-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{convertMember.full_name}</div>
                  <div className="text-xs text-slate-600">ITS {convertMember.its_id} · age {convertMember.age ?? '—'}</div>
                  {convertMember.hof_name && <div className="text-xs text-slate-500 truncate">HOF: {convertMember.hof_name}</div>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setConvertMember(null)}><X className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setConvertTarget(null)}>Cancel</Button>
            <Button disabled={!convertMember || busyConvert} onClick={submitConvert} data-testid="convert-submit"
              className="bg-emerald-600 hover:bg-emerald-700">
              {busyConvert ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Convert Seat</>}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Manual allot side drawer */}
      <Sheet open={!!manualSeat} onOpenChange={(o) => !o && setManualSeat(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="manual-allot-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Manual Allotment — Seat #{manualSeat?.seat_no}
            </SheetTitle>
            <SheetDescription>
              Use this for walk-in attendees not present in the database. The seat will be marked as <strong>MANUAL</strong> and the details printed on the pass.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="bg-purple-50 border border-purple-200 rounded p-3 text-xs">
              <div className="font-semibold text-purple-900 mb-1">Floor: {layout?.name}</div>
              <div className="text-purple-700">Seat #{manualSeat?.seat_no}</div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs font-semibold">Full Name *</Label>
                <Input value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                  placeholder="Visitor full name" data-testid="manual-name" autoFocus className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Mobile</Label>
                  <Input value={manualForm.mobile} onChange={(e) => setManualForm({ ...manualForm, mobile: e.target.value })}
                    placeholder="10-digit mobile" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-semibold">City</Label>
                  <Input value={manualForm.city} onChange={(e) => setManualForm({ ...manualForm, city: e.target.value })}
                    placeholder="e.g., Bombay" className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">ITS Number (optional)</Label>
                <Input value={manualForm.its_id} onChange={(e) => setManualForm({ ...manualForm, its_id: e.target.value })}
                  placeholder="If available" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Remarks</Label>
                <textarea value={manualForm.remarks} onChange={(e) => setManualForm({ ...manualForm, remarks: e.target.value })}
                  placeholder="Optional notes, relation to HOF, etc." rows={3}
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1" />
              </div>
            </div>
          </div>
          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setManualSeat(null)}>Cancel</Button>
            <Button disabled={!manualForm.name.trim()} onClick={submitManual} className="bg-purple-600 hover:bg-purple-700" data-testid="manual-submit">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Allot Seat
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Single Pass PDF preview */}
      <PdfPreviewModal
        open={!!passPreview}
        onClose={() => setPassPreview(null)}
        title={passPreview ? `Seat Pass — Seat #${passPreview.seat_no} · ${passPreview.member_name || ''}` : 'Seat Pass'}
        fileName={passPreview ? `seat_pass_${passPreview.seat_no}.pdf` : 'seat_pass.pdf'}
        fetcher={() => api.get(`/seat/bookings/${passPreview?.booking_id}/pass`, { responseType: 'blob' })}
      />

      {/* Bulk Pass PDF preview */}
      <PdfPreviewModal
        open={bulkPreview}
        onClose={() => setBulkPreview(false)}
        title={`All Passes — ${layout?.name || 'Floor'}`}
        fileName={`passes_${layout?.name || 'floor'}.pdf`}
        fetcher={() => api.get(`/seat/floors/${floorId}/passes-bulk`, { responseType: 'blob' })}
      />
    </div>
  );
}
